import { $, _ } from '../lib';
export const convertWikiTable = (value) => {
    try {
        const parsedTable = parseWikiTable(value);
        if (parsedTable == null) {
            return $('<pre/>').text(value).prop('outerHTML') ?? '';
        }
        return renderWikiTable(parsedTable);
    }
    catch (error) {
        console.error('Failed to render wikitable markdown block', error);
        return $('<pre/>').text(value).prop('outerHTML') ?? '';
    }
};
const parseWikiTable = (value) => {
    const lines = value.replace(/\r\n?/g, '\n').split('\n');
    const startIndex = lines.findIndex(line => line.trim() !== '');
    if (startIndex === -1) {
        return null;
    }
    const firstLine = lines[startIndex]?.trimStart() ?? '';
    if (!firstLine.startsWith('{|')) {
        return null;
    }
    const table = {
        attrs: parseWikiAttrs(firstLine.substring(2).trim()),
        rows: [],
    };
    let currentRow = null;
    let currentCell = null;
    for (const line of lines.slice(startIndex + 1)) {
        const trimmedLine = line.trimStart();
        if (trimmedLine.startsWith('|}')) {
            break;
        }
        if (trimmedLine.startsWith('|-')) {
            const rowRemainder = trimmedLine.substring(2).trimStart();
            currentRow = {
                attrs: rowRemainder.startsWith('|') || rowRemainder.startsWith('!')
                    ? {}
                    : parseWikiAttrs(rowRemainder),
                cells: [],
            };
            table.rows.push(currentRow);
            if (rowRemainder.startsWith('|') || rowRemainder.startsWith('!')) {
                const cells = parseWikiRowCells(rowRemainder);
                currentRow.cells.push(...cells);
                currentCell = _.last(cells) ?? null;
                continue;
            }
            currentCell = null;
            continue;
        }
        if (trimmedLine.startsWith('|+')) {
            table.caption = parseWikiCell(trimmedLine.substring(2), 'caption');
            currentCell = table.caption;
            continue;
        }
        if (trimmedLine.startsWith('!') || trimmedLine.startsWith('|')) {
            if (currentRow == null) {
                currentRow = {
                    attrs: {},
                    cells: [],
                };
                table.rows.push(currentRow);
            }
            const cells = parseWikiCells(trimmedLine);
            currentRow.cells.push(...cells);
            currentCell = _.last(cells) ?? null;
            continue;
        }
        if (currentCell != null) {
            currentCell.content = `${currentCell.content}\n${line}`;
        }
    }
    return table;
};
const renderWikiTable = (table) => {
    const $table = $('<table/>');
    applyWikiAttrs($table, table.attrs);
    if (table.caption != null) {
        $table.append(renderWikiTableCell(table.caption));
    }
    const headerRows = _.takeWhile(table.rows, row => row.cells.length > 0 && _.every(row.cells, cell => cell.tag === 'th'));
    const bodyRows = table.rows.slice(headerRows.length);
    if (headerRows.length > 0) {
        const $thead = $('<thead/>');
        _.forEach(headerRows, row => {
            $thead.append(renderWikiTableRow(row));
        });
        $table.append($thead);
    }
    if (bodyRows.length > 0 || headerRows.length === 0) {
        const $tbody = $('<tbody/>');
        _.forEach(bodyRows.length > 0 ? bodyRows : table.rows, row => {
            $tbody.append(renderWikiTableRow(row));
        });
        $table.append($tbody);
    }
    return $('<div/>').append($table).html() ?? '';
};
const parseWikiCell = (value, tag) => {
    const separatorIndex = findWikiPipe(value);
    if (separatorIndex === -1) {
        return {
            attrs: {},
            content: value.trimStart(),
            tag,
        };
    }
    return {
        attrs: parseWikiAttrs(value.slice(0, separatorIndex).trim()),
        content: value.slice(separatorIndex + 1).trimStart(),
        tag,
    };
};
const parseWikiCells = (line) => {
    const isHeader = line.startsWith('!');
    const tag = isHeader ? 'th' : 'td';
    const delimiter = isHeader ? '!!' : '||';
    return splitWikiSegments(line.substring(1), delimiter).map(segment => parseWikiCell(segment, tag));
};
const parseWikiRowCells = (line) => {
    const isHeader = line.startsWith('!');
    const repeatedDelimiter = isHeader ? '!!' : '||';
    if (line.includes(repeatedDelimiter)) {
        return parseWikiCells(line);
    }
    const tag = isHeader ? 'th' : 'td';
    const delimiter = isHeader ? '!' : '|';
    return splitWikiSegments(line.substring(1), delimiter).map(segment => ({
        attrs: {},
        content: segment.trimStart(),
        tag,
    }));
};
const parseWikiAttrs = (value) => {
    const attrs = {};
    const matches = value.matchAll(/([^\s=|]+)(?:\s*=\s*(?:"([^"]*)"|'([^']*)'|([^\s|]+)))?/g);
    for (const match of matches) {
        const attrName = match[1]?.trim() ?? '';
        if (!isSafeWikiAttrName(attrName) || /^on/i.test(attrName)) {
            continue;
        }
        attrs[attrName] = match[2] ?? match[3] ?? match[4] ?? '';
    }
    return attrs;
};
const applyWikiAttrs = ($elem, attrs) => {
    _.forEach(attrs, (attrValue, attrName) => {
        try {
            $elem.attr(attrName, attrValue);
        }
        catch (error) {
            console.error('Ignoring invalid wikitable attribute', attrName, error);
        }
    });
};
const renderWikiTableRow = (row) => {
    const $row = $('<tr/>');
    applyWikiAttrs($row, row.attrs);
    _.forEach(row.cells, cell => {
        $row.append(renderWikiTableCell(cell));
    });
    return $row[0];
};
const renderWikiTableCell = (cell) => {
    const $cell = $(`<${cell.tag}/>`);
    applyWikiAttrs($cell, cell.attrs);
    $cell.text(cell.content);
    return $cell[0];
};
const isSafeWikiAttrName = (value) => /^[A-Za-z_:][-A-Za-z0-9_:.]*$/.test(value);
const splitWikiSegments = (value, delimiter) => {
    const segments = [];
    let currentSegment = '';
    let quoteChar = null;
    let templateDepth = 0;
    let linkDepth = 0;
    for (let i = 0; i < value.length; i += 1) {
        const nextDelimiter = value.slice(i, i + delimiter.length);
        const nextPair = value.slice(i, i + 2);
        if (quoteChar == null &&
            templateDepth === 0 &&
            linkDepth === 0 &&
            nextDelimiter === delimiter) {
            segments.push(currentSegment);
            currentSegment = '';
            i += delimiter.length - 1;
            continue;
        }
        if (quoteChar == null && nextPair === '{{') {
            templateDepth += 1;
            currentSegment += nextPair;
            i += 1;
            continue;
        }
        if (quoteChar == null && nextPair === '}}' && templateDepth > 0) {
            templateDepth -= 1;
            currentSegment += nextPair;
            i += 1;
            continue;
        }
        if (quoteChar == null && nextPair === '[[') {
            linkDepth += 1;
            currentSegment += nextPair;
            i += 1;
            continue;
        }
        if (quoteChar == null && nextPair === ']]' && linkDepth > 0) {
            linkDepth -= 1;
            currentSegment += nextPair;
            i += 1;
            continue;
        }
        const char = value[i];
        if (quoteChar == null && (char === '"' || char === "'")) {
            quoteChar = char;
        }
        else if (quoteChar === char) {
            quoteChar = null;
        }
        currentSegment += char;
    }
    segments.push(currentSegment);
    return segments;
};
const findWikiPipe = (value) => {
    let quoteChar = null;
    let templateDepth = 0;
    let linkDepth = 0;
    for (let i = 0; i < value.length; i += 1) {
        const nextPair = value.slice(i, i + 2);
        if (quoteChar == null && nextPair === '{{') {
            templateDepth += 1;
            i += 1;
            continue;
        }
        if (quoteChar == null && nextPair === '}}' && templateDepth > 0) {
            templateDepth -= 1;
            i += 1;
            continue;
        }
        if (quoteChar == null && nextPair === '[[') {
            linkDepth += 1;
            i += 1;
            continue;
        }
        if (quoteChar == null && nextPair === ']]' && linkDepth > 0) {
            linkDepth -= 1;
            i += 1;
            continue;
        }
        const char = value[i];
        if (quoteChar == null && (char === '"' || char === "'")) {
            quoteChar = char;
            continue;
        }
        if (quoteChar === char) {
            quoteChar = null;
            continue;
        }
        if (quoteChar == null && templateDepth === 0 && linkDepth === 0 && char === '|') {
            return i;
        }
    }
    return -1;
};
//# sourceMappingURL=markdown-wikitable.js.map